import * as idefs from './checkSchemaDefs'
import { ITraverseCallback, ITraverseOptions, IValueType, SchemaManager } from './schemaManager'
import { IComponent, IContainerComponent, IDataTableComponent, ISchema, ISchemaError } from './types'
import * as errs from './checkSchemaErrs'
import cloneDeep from 'lodash.clonedeep'

export interface InterfacePropDef {
    key: string
    type: string
    required?: boolean
}

export interface InterfaceDef {
    interface: string
    extends: string[]
    props: InterfacePropDef[]
}

const InterfaceDefs: InterfaceDef[] = Object.values(idefs).map(o => o)

let errors: ISchemaError[] = []

export const checkSchema = (_schema: ISchema, ignoreTypes?: string[], checkNameOrField?: boolean): ISchemaError[] => {
    const schema = cloneDeep(_schema)
    errors = []
    const doubleFields: string[] = []
    const doubleNames: string[] = []

    if (SchemaManager.checkValueType(schema) !== IValueType.component) {
        addErr(schema, errs.schema)
        return errors
    }
    traverseSchema((comp, parent) => {
        if (typeof comp === 'object') comp.parentComp = parent;
    }, schema)

    traverseSchema(comp => {
        if (comp['field']) {
            const dt = getParentDataTable(comp)
            if (dt) {
                comp['field'] = dt['field'] + '.' + comp['field']
            }
        }
    }, schema)


    traverseSchema(comp => {
        if (SchemaManager.checkValueType(comp) !== IValueType.component) {
            addErr(comp, errs.notype)
        } else {
            const def = getInterfaceDefByType(comp.type)
            if (!def) {
                if (ignoreTypes && ignoreTypes.indexOf(comp.type) > -1) {

                } else {
                    addErr(comp, errs.typeunknown(comp.type))
                }
            } else {
                if (comp === schema) {
                    def.props.push(...idefs.ISchemaProps_Def.props)
                }
                def.props.forEach(prop => {
                    if (typeof comp[prop.key] === 'undefined') {
                        if (prop.required) addErr(comp, errs.requiredpropmissing(prop.key))
                    } else {
                        checkPropValueType(comp, prop)
                    }
                })
                Object.keys(comp).forEach(prop => {
                    const defprop = def.props.find(p => p.key === prop)
                    if (!defprop) {
                        addErr(comp, errs.unknownprop(prop), true)
                    }
                })

            }
            if (checkNameOrField) {
                if (!comp.name && !comp['field']) {
                    addErr(comp, errs.noNameOrField, true)
                }
            }
            if (comp.name) {
                if (doubleNames.indexOf(comp.name) > -1) {
                    addErr(comp, errs.doubleName(comp.name))
                } else {
                    doubleNames.push(comp.name)
                }
            }
            if (comp['field']) {
                if (doubleFields.indexOf(comp['field']) > -1) {
                    addErr(comp, errs.doubleField(comp['field']))
                } else {
                    doubleFields.push(comp['field'])
                }
            }
            if (comp.type === 'datatable') {
                if (comp.detailComponent) {
                    let hasfield = false
                    let hasChildDatatable = false
                    traverseSchema(c => {
                        if (c['field']) {
                            hasfield = true
                        }
                        if (c.type === 'datatable') {
                            hasChildDatatable = true
                        }
                    }, comp.detailComponent)
                    if (!hasfield) {
                        addErr(comp, errs.datatablenofields)
                    }
                    if (hasChildDatatable) {
                        addErr(comp, errs.datatableindatatable)
                    }
                }
                if (comp.cardView && !comp.summaryCard) {
                    addErr(comp, errs.datatablenosummary, true)
                }

            }
        }
    }, schema)

    return errors
}

const checkPropValueType = (comp: IComponent, propDef: InterfacePropDef,) => {
    const prop = comp[propDef.key]
    if (propDef.type === 'string' || propDef.type === 'number' || propDef.type === 'boolean') {
        if (typeof prop !== propDef.type) {
            addErr(comp, errs.wrongproptype(propDef.key, propDef.type))
            return
        }
        if (propDef.key === 'field') {
            if ((prop as string).indexOf(' ') > -1) {
                addErr(comp, errs.fieldName(prop))
            }
        }

    } else if (propDef.type === 'IComponent') {
        if (SchemaManager.checkValueType(prop) !== IValueType.component) {
            addErr(comp, errs.wrongproptype(propDef.key, propDef.type))
        }
    } else if (propDef.type.indexOf('[]') > -1) {
        if (SchemaManager.checkValueType(prop) !== IValueType.array) {
            addErr(comp, errs.wrongproptype(propDef.key, propDef.type))
            return
        }

        if (prop.length === 0) {
            addErr(comp, errs.arraempty(propDef.key), true)
            return
        }
        prop.forEach((elem: any) => {
            if (propDef.type === 'string[]') {
                if (typeof elem !== 'string') {
                    addErr(comp, errs.wrongproptypearray(propDef.key, propDef.type))
                    return
                }
            }
            if (propDef.type === 'IComponent[]') {
                if (SchemaManager.checkValueType(elem) !== IValueType.component) {
                    addErr(comp, errs.wrongproptypearray(propDef.key, propDef.type))
                    return
                }
            }
            if (propDef.type === 'string[] | ISelectOptionItem[]') {
                const t = SchemaManager.checkValueType(elem)
                if (t === IValueType.string || t === IValueType.object) {
                    if (t === IValueType.object) {
                        if (typeof elem['label'] === 'undefined' || typeof elem['value'] === 'undefined') {
                            addErr(comp, errs.wrongproptypearraychilren)
                            return
                        }
                    }
                } else {
                    addErr(comp, errs.wrongproptypearray(propDef.key, propDef.type))
                    return
                }
            }

        });
    } else if (propDef.type.indexOf('|') > -1) {
        if (typeof prop !== 'string') {
            addErr(comp, errs.wrongproptype(propDef.key, propDef.type))
            return
        }
        const arr = propDef.type.split('|').map(s => s.trim())
        if (arr.indexOf(prop) === -1) {
            addErr(comp, errs.wrongproptype(propDef.key, propDef.type))
        }
    } else if (propDef.type.indexOf('&') > -1) {
        if (typeof prop !== 'string') {
            addErr(comp, errs.wrongproptype(propDef.key, propDef.type))
            return
        }
        const colsbpDef = ['xs', 'sm', 'md', 'lg', 'xl', 'xx']
        const arrdef = propDef.type.split('&').map(s => s.trim())
        const arrprop = prop.split(' ').map(s => s.trim())
        let erroradded: boolean = false
        arrprop.forEach(p => {
            if (!erroradded && p && arrdef.indexOf(p) === -1) {
                let type = propDef.type
                if (propDef.key === 'cols') {
                    type = 'strings of ICols, separated by spaces'
                }
                addErr(comp, errs.wrongproptype(propDef.key, type))
                erroradded = true
            }
        })
        if (propDef.key === 'cols') {
            colsbpDef.forEach(bp => {
                const l = arrprop.filter(p => p.substring(0, 2) === bp).length
                if (l > 1) {
                    addErr(comp, errs.doublebp(bp), true)
                }
            })

        }
    }

}


const mergeExtendsInterfaceDef = (interfaceDef: InterfaceDef, origInterfaceDef: InterfaceDef) => {
    interfaceDef.extends.forEach(ext => {
        const idef = InterfaceDefs.find(d => d.interface === ext)
        if (idef) {
            origInterfaceDef.props.push(...idef.props)
            mergeExtendsInterfaceDef(idef, origInterfaceDef)
        }
    })
}

const getInterfaceDefByType = (type: string): InterfaceDef | undefined => {
    let found: InterfaceDef | undefined
    InterfaceDefs.forEach(d => {
        if (found) return
        const prop = d.props.find(p => p.key === 'type' && p.type === type)
        if (prop) found = cloneDeep(d)
    })
    if (found) {
        mergeExtendsInterfaceDef(found, found)
    }
    return found
}

const traverseSchema = (fn: ITraverseCallback, comp: IComponent, parentComp?: IComponent, options?: ITraverseOptions) => {
    if (options && options.done) return;

    fn(comp, parentComp, options);
    const ctcomp = comp as IContainerComponent
    if (ctcomp && ctcomp.children) {
        ctcomp.children.forEach(c => traverseSchema(fn, c, comp, options));
    }
    Object.keys(comp).forEach(prop => {
        if (comp && prop.indexOf('Component') > - 1) {
            const cp = comp[prop]
            if (SchemaManager.checkValueType(cp) === IValueType.component) {
                traverseSchema(fn, cp, comp, options)
            }
        }
    })
}

const addErr = (comp: IComponent, error: string, isWarning?: boolean) => {
    const type = isWarning ? 'warning' : 'error'
    errors.push({ type, comp, error })
}

const getParentDataTable = (comp: IComponent): IDataTableComponent | undefined => {
    let p = comp.parentComp;
    while (p) {
        if (p.type === 'datatable') {
            return p as IDataTableComponent;
        }
        p = p.parentComp;
    }
    return undefined;
}



export const printErrors = (schemaName: string, errors: ISchemaError[]) => {
    const printComp = (c: IComponent): string => {
        return `${c.name ? 'Name: ' + c.name + ' ' : ''}${c.type ? 'Type: ' + c.type + ' ' : ''}${c['field'] ? 'Field: ' + c['field'] : ''}`
    }
    const errArr: string[] = []
    errArr.push(' ')
    errArr.push(`Schema-Errors: ${schemaName}`)
    errArr.push('='.repeat(errArr[1].length))
    errArr.push(' ')
    if (errors.length === 0) {
        errArr.push('No errors!')
    }
    const errors_errors = errors.filter(e => e.type === 'error')
    if (errors_errors.length > 0) {
        errArr.push('Errors:')
        errArr.push('=======')
        errArr.push(' ')
        errors_errors.forEach(e => {
            errArr.push(e.error)
            errArr.push(printComp(e.comp))
            errArr.push(' ')
        })
    }

    const errors_warnings = errors.filter(e => e.type === 'warning')
    if (errors_warnings.length > 0) {
        errArr.push('Warnings:')
        errArr.push('=========')
        errArr.push(' ')
        errors_warnings.forEach(e => {
            errArr.push(e.error)
            errArr.push(printComp(e.comp))
            errArr.push(' ')
        })
    }

    console.log(errArr.join('\n'))

}